type QuickReplyBearingMessage = {
	role: 'user' | 'assistant' | 'event' | 'tool';
	quickReplies?: readonly unknown[];
};

/**
 * Status and tool rows are informational, not conversation turns. Keep the
 * latest assistant choice active across those rows, but retire it as soon as
 * the user responds or a newer assistant message takes over.
 */
export const findActiveQuickReplyMessageIndex = (
	messages: readonly QuickReplyBearingMessage[]
): number => {
	for (let index = messages.length - 1; index >= 0; index -= 1) {
		const message = messages[index];
		if (message.role === 'event' || message.role === 'tool') continue;
		if (message.role === 'assistant') {
			return message.quickReplies?.length ? index : -1;
		}
		return -1;
	}
	return -1;
};

/** Find the opening direction choices, allowing informational rows before them. */
export const findOpeningQuickReplyMessageIndex = (
	messages: readonly QuickReplyBearingMessage[]
): number => {
	for (let index = 0; index < messages.length; index += 1) {
		const message = messages[index];
		if (message.role === 'event' || message.role === 'tool') continue;
		return message.role === 'assistant' && message.quickReplies?.length ? index : -1;
	}
	return -1;
};

/**
 * The compact goal reminder is a fallback for displaced opening actions, not a
 * second copy of them. Informational status/tool rows do not displace the
 * opening question; a later conversational message does.
 */
export const shouldShowDirectionPrompt = (
	messages: readonly QuickReplyBearingMessage[]
): boolean => {
	const openingIndex = findOpeningQuickReplyMessageIndex(messages);
	if (openingIndex < 0) return false;
	return findActiveQuickReplyMessageIndex(messages) !== openingIndex;
};

/** Flowy may move board focus only after an explicit goal produced a plan. */
export const shouldAllowFlowyAutoSelectNodes = ({
	hasGoal,
	hasPlan,
}: {
	hasGoal: boolean;
	hasPlan: boolean;
}): boolean => hasGoal && hasPlan;
